import { TFunction, Trans } from '@lyearn/i18n';
import { SparkleOutline, TrendingUpFill } from '@lyearn/icons';
import { DateFilterListItem, DateFilterListItemKey } from '@/components/DateFilter/constants';
import DateRangeFilter from '@/components/FilterManager/DateRangeFilter';
import { getSelectedOperatorTillBESupport } from '@/components/FilterManager/SortAndFilter/components/SortAndFilter';
import {
  getAlphabeticallySortItem,
  getDefaultSortValue,
  getLastEditedSortItem,
  getNewestSortItem,
  getOldestSortItem,
} from '@/components/FilterManager/SortMenu/utils';
import { FilterManager, SortAndFilterItem, SortEnums } from '@/components/FilterManager/types';
import { FeatureFlagsMap } from '@/helper/FeatureFlags';
import { AppConfig } from '@/hooks/useAppConfig';
import { getSearchContentTypeOptions } from '@/modules/corporate/modules/admin/views/LearningContent/helpers/getContentType';
import { DateFilter, SearchableContentTypeEnum } from '@/types/schema';
import { OrderEnum } from '@/types/schema';

export const getIsDataEmpty = (fetching: boolean, data: any, dataLessons: any) =>
  !fetching &&
  data.contents.totalCount === 0 &&
  data.events.totalCount === 0 &&
  dataLessons.lessons.totalCount === 0;

export const MOST_ACCESSED_ID = 'mostAccessed';

export const SEARCH_DATE_FILTER_OPTIONS: DateFilterListItem[] = [
  { key: DateFilterListItemKey.ThisWeek, label: 'LearnerProfile:Activities.ThisWeek' },
  { key: DateFilterListItemKey.LastWeek, label: 'LearnerProfile:Activities.LastWeek' },
  { key: DateFilterListItemKey.ThisMonth, label: 'LearnerProfile:Activities.ThisMonth' },
  { key: DateFilterListItemKey.LastMonth, label: 'LearnerProfile:Activities.LastMonth' },
  { key: DateFilterListItemKey.Custom, label: 'Common:PickDateRange' },
];

export const SEARCH_SORT_ITEMS = [
  getNewestSortItem(),
  {
    Icon: SparkleOutline,
    id: SortEnums.Relevance,
    label: <Trans i18nKey="Common:Sort.Relevance" />,
    sort: [{ field: '_score', order: OrderEnum.Desc }],
  },
  {
    Icon: TrendingUpFill,
    id: MOST_ACCESSED_ID,
    label: <Trans i18nKey="Common:Sort.MostPopular" />,
    sort: [{ field: MOST_ACCESSED_ID, order: OrderEnum.Desc }],
  },
  getLastEditedSortItem(),
  getAlphabeticallySortItem(),
  getOldestSortItem(),
];

export const DEFAULT_SEARCH_SORT_ITEM = getDefaultSortValue(SEARCH_SORT_ITEMS);

export const getSearchSortAndFilterItems = (
  t: TFunction,
  isMostAccessedSelected: boolean,
  isSessionEnabled: boolean,
  featureFlag: FeatureFlagsMap,
  appConfig: AppConfig,
  allowedContentTypes: Set<SearchableContentTypeEnum>,
): SortAndFilterItem[] => {
  return [
    {
      type: FilterManager.Sort,
      isEnabled: true,
      items: SEARCH_SORT_ITEMS,
    },
    {
      type: FilterManager.Custom,
      isEnabled: isMostAccessedSelected,
      renderer: ({ filter, setFilter }) => {
        const selectedValue = filter.createdAt as DateFilter;

        const onChange = (value?: DateFilter) => {
          setFilter({ ...filter, createdAt: value });
        };

        return (
          <DateRangeFilter
            defaultPlaceholder={t('Common:Filter.TimePeriod')}
            options={SEARCH_DATE_FILTER_OPTIONS}
            selectedOperator={getSelectedOperatorTillBESupport(t)}
            selectedValue={selectedValue}
            onChange={onChange}
          />
        );
      },
    },
    {
      type: FilterManager.Divider,
      isEnabled: isMostAccessedSelected,
    },
    {
      type: FilterManager.Filter,
      id: 'contentTypes',
      defaultPlaceholder: t('Common:Filter.TypeOfContent'),
      isEnabled: true,
      groups: [
        {
          id: 'resourceType',
          options: getSearchContentTypeOptions(
            t,
            isSessionEnabled,
            featureFlag,
            appConfig,
            allowedContentTypes,
          ),
        },
      ],
    },
  ];
};
